// Jeu d'icônes original de l'interface (traits droits, angles vifs, esprit atelier) et pictogrammes des devises.
// Toutes les icônes héritent de la couleur du texte ; aucune ressource externe.
import type { ReactNode, SVGProps } from 'react';
import { formatNumber } from '../i18n';

export type IconName =
  | 'hangar'
  | 'tree'
  | 'pass'
  | 'shop'
  | 'friends'
  | 'profile'
  | 'settings'
  | 'help'
  | 'close'
  | 'left'
  | 'right'
  | 'up'
  | 'down'
  | 'lock'
  | 'check'
  | 'copy'
  | 'crown'
  | 'play'
  | 'exit'
  | 'chat'
  | 'mail'
  | 'star'
  | 'bolt'
  | 'restore'
  | 'wrench'
  | 'key'
  | 'target'
  | 'plus'
  | 'history'
  | 'swap'
  | 'alert';

const PATHS: Record<IconName, ReactNode> = {
  hangar: (
    <>
      <path d="M3 20V9l9-5 9 5v11" />
      <path d="M7 20v-8h10v8M7 15.5h10" />
    </>
  ),
  tree: (
    <>
      <rect x="3" y="3" width="5" height="5" />
      <rect x="16" y="3" width="5" height="5" />
      <rect x="16" y="16" width="5" height="5" />
      <path d="M8 5.5h8M5.5 8v10.5H16" />
    </>
  ),
  pass: <path d="M5 9.5l7-5 7 5M5 14.5l7-5 7 5M5 19.5l7-5 7 5" />,
  shop: (
    <>
      <rect x="4" y="7" width="16" height="13" />
      <path d="M4 7l16 13M20 7L4 20M9 7V4h6v3" />
    </>
  ),
  friends: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20.5c.7-3.6 3.2-5.6 6.5-5.6s5.8 2 6.5 5.6M15.6 4.7a3.5 3.5 0 0 1 0 6.6M17.6 15c2 .7 3.3 2.6 3.9 5.5" />
    </>
  ),
  profile: (
    <>
      <path d="M6 12a6 6 0 0 1 12 0v3H6z" />
      <path d="M6 12.5h12M8 15v5h8v-5" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.3 5.3l2.1 2.1M16.6 16.6l2.1 2.1M5.3 18.7l2.1-2.1M16.6 7.4l2.1-2.1" />
      <circle cx="12" cy="12" r="6.5" />
    </>
  ),
  help: (
    <>
      <path d="M12 2.5l8.2 4.75v9.5L12 21.5l-8.2-4.75v-9.5z" />
      <path d="M9.6 9.6a2.5 2.5 0 1 1 3.4 2.3c-.6.3-1 .8-1 1.5v.6M12 16.6v.4" />
    </>
  ),
  close: <path d="M6 6l12 12M18 6L6 18" />,
  left: <path d="M15 4.5L7.5 12l7.5 7.5" />,
  right: <path d="M9 4.5l7.5 7.5L9 19.5" />,
  up: <path d="M4.5 15l7.5-7.5 7.5 7.5" />,
  down: <path d="M4.5 9l7.5 7.5 7.5-7.5" />,
  lock: (
    <>
      <rect x="5" y="10.5" width="14" height="10" />
      <path d="M8.5 10.5V7.5a3.5 3.5 0 0 1 7 0v3M12 14.5v2" />
    </>
  ),
  check: <path d="M4.5 12.5l4.5 4.5L19.5 6.5" />,
  copy: (
    <>
      <rect x="8.5" y="8.5" width="11.5" height="11.5" />
      <path d="M4 15.5V4h11.5" />
    </>
  ),
  crown: <path d="M3.5 18h17l-1.6-10.5-4.4 4-2.5-6.5-2.5 6.5-4.4-4z" />,
  play: <path d="M7 4.5v15l12-7.5z" fill="currentColor" />,
  exit: <path d="M10 4H4.5v16H10M14.5 8l4 4-4 4M18.5 12H9" />,
  chat: <path d="M4 5h16v11H9.5L4 20z" />,
  mail: (
    <>
      <rect x="3" y="5.5" width="18" height="13" />
      <path d="M3 5.5l9 7.5 9-7.5" />
    </>
  ),
  star: <path d="M12 3.5l2.6 5.5 6 .7-4.5 4.1 1.2 5.9L12 16.8l-5.3 2.9 1.2-5.9-4.5-4.1 6-.7z" />,
  bolt: <path d="M13.5 2.5L5 13.5h6l-1 8 8.5-11h-6z" />,
  restore: (
    <>
      <path d="M4.5 12a7.5 7.5 0 1 0 2.2-5.3" />
      <path d="M4 3.5v4.5h4.5" />
    </>
  ),
  wrench: <path d="M14.5 3.5a5 5 0 0 0-4.6 6.9L3.5 16.8l3.7 3.7 6.4-6.4a5 5 0 0 0 6.9-4.6l-3 3-3.2-.8-.8-3.2z" />,
  key: (
    <>
      <circle cx="8" cy="15.5" r="4.5" />
      <path d="M11.2 12.3L20 3.5M16 7.5l2.5 2.5M18.5 5l2 2" />
    </>
  ),
  target: (
    <>
      <circle cx="12" cy="12" r="7.5" />
      <path d="M12 2v5M12 17v5M2 12h5M17 12h5" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  history: (
    <>
      <path d="M4 12a8 8 0 1 0 2.4-5.7" />
      <path d="M4 4v4.5h4.5M12 8v4.5l3 2" />
    </>
  ),
  swap: <path d="M4 8.5h14l-3.5-3.5M20 15.5H6l3.5 3.5" />,
  alert: <path d="M12 3.5L21.5 20h-19zM12 9.5v5M12 17v.5" />,
};

export function Icon({ name, size = 20, ...rest }: { name: IconName; size?: number } & SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="square"
      strokeLinejoin="miter"
      aria-hidden
      focusable="false"
      className="icon"
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}

/** Pièce : disque de laiton frappé d'un écrou hexagonal. */
export function CoinIcon({ size = 18 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden focusable="false" className="cur-icon">
      <circle cx="12" cy="12" r="9.5" fill="#e0b04a" stroke="#6e5016" strokeWidth="1.5" />
      <circle cx="12" cy="12" r="7" fill="none" stroke="#f3d27c" strokeWidth="1" opacity=".7" />
      <path d="M12 7.6l3.8 2.2v4.4L12 16.4l-3.8-2.2V9.8z" fill="#b8892c" stroke="#6e5016" strokeWidth="1.2" />
      <circle cx="12" cy="12" r="1.6" fill="#6e5016" />
    </svg>
  );
}

/** Gemme : cristal hexagonal taillé. */
export function GemIcon({ size = 18 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden focusable="false" className="cur-icon">
      <path d="M12 2.5l7.8 4.6v9.8L12 21.5l-7.8-4.6V7.1z" fill="#6fb9d4" stroke="#1f4a5a" strokeWidth="1.4" />
      <path d="M12 2.5l7.8 4.6L12 11.6 4.2 7.1z" fill="#a9dcee" stroke="#1f4a5a" strokeWidth="1" />
      <path d="M12 11.6v9.9" stroke="#1f4a5a" strokeWidth="1" opacity=".6" />
    </svg>
  );
}

/** Montant avec son pictogramme (pièces ou gemmes), chiffres alignés. */
export function Amount({ kind, n, size }: { kind: 'coins' | 'gems'; n: number; size?: number }) {
  return (
    <span className={`amount amount-${kind}`}>
      {kind === 'coins' ? <CoinIcon size={size} /> : <GemIcon size={size} />}
      <span className="amount-n">{formatNumber(n)}</span>
    </span>
  );
}

/** Icône d'un jeton (boost, restauration, réinitialisation, salon) dans une puce hexagonale. */
export function TokenIcon({ token, size = 28 }: { token: string; size?: number }) {
  const glyph: Record<string, ReactNode> = {
    tok_x2: (
      <text x="12" y="15.6" textAnchor="middle" fontSize="9.5" fontWeight="800" fill="currentColor" stroke="none">
        ×2
      </text>
    ),
    tok_x5: (
      <text x="12" y="15.6" textAnchor="middle" fontSize="9.5" fontWeight="800" fill="currentColor" stroke="none">
        ×5
      </text>
    ),
    tok_restore: <path d="M7.5 12a4.5 4.5 0 1 0 1.3-3.2M7.2 6.6v2.6h2.6" />,
    tok_reset: <path d="M14.2 7a3 3 0 0 0-2.8 4.1L7.5 15l1.6 1.6 3.9-3.9a3 3 0 0 0 4.1-2.8l-1.8 1.8-1.9-.5-.5-1.9z" />,
    tok_salon: (
      <>
        <circle cx="9.5" cy="13.8" r="2.6" />
        <path d="M11.4 11.9l5-5M14.4 8.9l1.5 1.5" />
      </>
    ),
  };
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden
      focusable="false"
      className={`token-icon token-${token}`}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="square"
    >
      <path d="M12 1.8l8.8 5.1v10.2L12 22.2l-8.8-5.1V6.9z" className="token-plate" />
      {glyph[token] ?? null}
    </svg>
  );
}

/** Pictogrammes des modes de jeu (sélecteur du hangar). */
export function ModeIcon({ mode }: { mode: string }) {
  const tank = (x: number, y: number, a: number, cls = 'mi-a') => (
    <g transform={`translate(${x} ${y}) rotate(${a})`} className={cls}>
      <rect x="-1.5" y="-1.6" width="6.5" height="3.2" />
      <circle r="3.6" />
    </g>
  );
  return (
    <svg viewBox="0 0 40 40" width="40" height="40" aria-hidden focusable="false" className="mode-icon">
      <rect x="1" y="1" width="38" height="38" className="mi-bg" />
      {mode === 'ffa' && (
        <>
          {tank(11, 12, 35)}
          {tank(29, 11, 150, 'mi-b')}
          {tank(13, 29, -40, 'mi-b')}
          {tank(28, 28, -135, 'mi-b')}
        </>
      )}
      {mode === 'tdm' && (
        <>
          {tank(9, 13, 0)}
          {tank(9, 27, 0)}
          {tank(31, 13, 180, 'mi-b')}
          {tank(31, 27, 180, 'mi-b')}
          <path d="M20 6v28" className="mi-line" />
        </>
      )}
      {mode === 'dom' && (
        <>
          <circle cx="9" cy="28" r="5" className="mi-ring" />
          <circle cx="20" cy="11" r="5" className="mi-ring mi-ring-on" />
          <circle cx="31" cy="28" r="5" className="mi-ring" />
          <text x="9" y="30.8" className="mi-txt">A</text>
          <text x="20" y="13.8" className="mi-txt">B</text>
          <text x="31" y="30.8" className="mi-txt">C</text>
        </>
      )}
      {mode === 'survival' && (
        <>
          <path d="M6 20a14 14 0 0 1 28 0M10 20a10 10 0 0 1 20 0" className="mi-line" />
          {tank(20, 25, -90)}
          <path d="M5 33h30" className="mi-line" />
        </>
      )}
      {mode === 'private' && (
        <>
          <rect x="10" y="17" width="20" height="15" className="mi-ring" />
          <path d="M14 17v-4a6 6 0 0 1 12 0v4" className="mi-line" />
          <path d="M20 22v5" className="mi-line" />
        </>
      )}
      {mode === 'forge' && (
        <>
          {/* Enclume et tank amélioré : la Forge. */}
          <path d="M8 26h24l-4 4H12zM15 30v4M25 30v4M12 34h16" className="mi-line" />
          {tank(20, 15, -90)}
          <path d="M28 8l3-3M31 11l4-1M25 6l-1-4" className="mi-line" />
        </>
      )}
    </svg>
  );
}
